// Per-run visual style for the compare board. Color is treated as a
// REDUNDANT cue, not the primary identifier — the primary identifier is
// the shape glyph (in tables/labels) or the marker shape / dash pattern /
// fill pattern (in charts). This is a hard project rule because the
// author of this dashboard has severe protan CVD; multi-shade red/green
// gradients and color-only legends are unreadable for him.

// Okabe-Ito palette, dark-bg-tuned. Ordering picked so that adjacent
// indices are maximally distinguishable for protan/deutan CVD: no
// purple-next-to-blue, no yellow-next-to-green. Anchored on
// blue + orange (the two hues that stay clearly distinct for the user).
// Past index 7 the palette repeats, but the shape / dash / pattern
// cycles below have prime-ish lengths so the (color, shape, dash, pattern)
// tuple stays unique for many more runs than any single channel alone.
const COLORS = [
  "#56B4E9", // sky blue   — primary anchor
  "#E69F00", // orange     — primary contrast
  "#F0E442", // yellow
  "#009E73", // bluish green
  "#D55E00", // vermillion
  "#CC79A7", // reddish purple
  "#FFFFFF", // white      — replaces "black" on dark bg
  "#94A3B8", // slate gray — neutral fallback
];

// Recharts ScatterChart accepts these built-in shape names.
const SHAPES = [
  "circle",
  "square",
  "triangle",
  "diamond",
  "star",
  "cross",
  "wye",
] as const;

// Unicode glyphs to use INLINE in tables, legends, and run-label chips.
// Matched 1:1 with SHAPES so the legend chip and the chart marker agree.
const GLYPHS = ["●", "■", "▲", "◆", "★", "✚", "Y"];

// SVG stroke-dasharray strings. "" = solid. Mixed so adjacent cycle
// indices read clearly even in monochrome.
const DASHES = [
  "",          // solid
  "8 4",       // long dash
  "2 3",       // dotted
  "10 3 2 3",  // dash-dot
  "4 2 1 2",   // dash-dot-dot
];

// SVG <pattern> id suffix — defined once on the page (see RunStylePatternDefs).
const PATTERNS = [
  "solid",
  "diag",
  "cross",
  "dots",
  "vstripe",
];

export interface RunStyle {
  color: string;
  shape: typeof SHAPES[number];
  glyph: string;
  dash: string;
  pattern: string;
}

export function runStyle(idx: number): RunStyle {
  // Use independent moduli (prime-ish lengths) so the per-channel cycles
  // don't all roll over together — keeps tuples unique for 30+ runs even
  // though each individual channel only has 5-8 values.
  return {
    color: COLORS[idx % COLORS.length],
    shape: SHAPES[idx % SHAPES.length],
    glyph: GLYPHS[idx % GLYPHS.length],
    dash: DASHES[idx % DASHES.length],
    pattern: PATTERNS[idx % PATTERNS.length],
  };
}

/** Color-only shortcut for the many existing call sites that already have
 *  per-run text styling and just need the hex. New call sites should
 *  prefer `runStyle()` so they also pick up the shape/glyph/dash. */
export function runColor(idx: number): string {
  return runStyle(idx).color;
}

/** Human-readable run label. Prefers the saved-config name stamped on
 *  the run (`config.feature_config_label`); falls back to "default" for
 *  inline/no-config runs. Always pair with the run_id in smaller text so
 *  navigation still works. */
export function runLabel(entry: { config?: Record<string, unknown> | null }): string {
  const cfg = (entry.config ?? {}) as Record<string, unknown>;
  const lbl = cfg.feature_config_label as string | undefined;
  return (lbl && lbl.trim()) || "default";
}

export const RUN_PATTERN_IDS = PATTERNS;
